import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    ChevronRight, ChevronLeft, Maximize, Minimize, X,
    Server, Shield, Cloud, Cpu, Database, Network,
    MapPin, Lock, Activity, Users, Zap, Globe,
    CheckCircle2, ArrowRight, Play, Monitor, Sun, Moon,
    Droplets, Landmark, Radio, Building2, Briefcase,
    TrendingUp, BarChart3, Clock, Target, Layers,
    GitBranch, ArrowUpRight, Gauge, RefreshCw, Eye,
    Wand2, Bot, Code2, HardDrive
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const PresentationMode = () => {
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();
    const isDarkMode = theme === 'dark';
    
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const containerRef = useRef(null);

    const totalSlides = 23;

    const goToHome = () => navigate('/');

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (isTransitioning) return;
            switch (e.key) {
                case 'ArrowRight': case 'ArrowDown': case ' ':
                    e.preventDefault(); nextSlide(); break;
                case 'ArrowLeft': case 'ArrowUp':
                    e.preventDefault(); prevSlide(); break;
                case 'f': case 'F': toggleFullscreen(); break;
                case 'Escape':
                    if (!document.fullscreenElement) goToHome(); break;
                case 'Home': setCurrentSlide(0); break;
                case 'End': setCurrentSlide(totalSlides - 1); break;
                default: break;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isTransitioning]);

    useEffect(() => {
        let wheelTimeout;
        const handleWheel = (e) => {
            if (isTransitioning) return;
            clearTimeout(wheelTimeout);
            wheelTimeout = setTimeout(() => {
                if (e.deltaY > 50) nextSlide();
                else if (e.deltaY < -50) prevSlide();
            }, 50);
        };
        window.addEventListener('wheel', handleWheel, { passive: true });
        return () => window.removeEventListener('wheel', handleWheel);
    }, [isTransitioning]);

    useEffect(() => {
        let touchStartY = 0, touchStartX = 0;
        const handleTouchStart = (e) => { touchStartY = e.touches[0].clientY; touchStartX = e.touches[0].clientX; };
        const handleTouchEnd = (e) => {
            if (isTransitioning) return;
            const diffY = touchStartY - e.changedTouches[0].clientY;
            const diffX = touchStartX - e.changedTouches[0].clientX;
            if (Math.abs(diffY) > Math.abs(diffX)) { if (diffY > 50) nextSlide(); else if (diffY < -50) prevSlide(); }
            else { if (diffX > 50) nextSlide(); else if (diffX < -50) prevSlide(); }
        };
        window.addEventListener('touchstart', handleTouchStart);
        window.addEventListener('touchend', handleTouchEnd);
        return () => { window.removeEventListener('touchstart', handleTouchStart); window.removeEventListener('touchend', handleTouchEnd); };
    }, [isTransitioning]);

    const nextSlide = useCallback(() => {
        if (currentSlide < totalSlides - 1) { setIsTransitioning(true); setCurrentSlide(prev => prev + 1); setTimeout(() => setIsTransitioning(false), 600); }
    }, [currentSlide, totalSlides]);

    const prevSlide = useCallback(() => {
        if (currentSlide > 0) { setIsTransitioning(true); setCurrentSlide(prev => prev - 1); setTimeout(() => setIsTransitioning(false), 600); }
    }, [currentSlide]);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) { containerRef.current?.requestFullscreen(); setIsFullscreen(true); }
        else { document.exitFullscreen(); setIsFullscreen(false); }
    };

    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    const goToSlide = (index) => {
        if (index !== currentSlide && !isTransitioning) { setIsTransitioning(true); setCurrentSlide(index); setTimeout(() => setIsTransitioning(false), 600); }
    };

    const controlBgClass = isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-slate-100 hover:bg-slate-200';
    const controlBorderClass = isDarkMode ? 'border-white/10' : 'border-slate-300';

    const slides = [
        WelcomeSlide, DNSOverviewSlide, DNSServicesSlide, PartnersSlide,
        CustomersBankingGovSlide, CustomersEnterpriseTechSlide,
        HeroSlide, ByTheNumbersSlide, WhyEverestCloudSlide, IndustriesSlide,
        AvailabilityZonesSlide, ArchitectureSlide, InfraScaleSlide,
        PerformanceSlide, NetworkPeeringSlide, ProductsSlide,
        SecuritySlide, OperationsSlide, SLASlide,
        GreenEnergySlide, MigrationSlide, PlannerSlide, ClosingSlide
    ];

    const SlideComponent = slides[currentSlide];

    return (
        <div ref={containerRef} className={`fixed inset-0 ${isDarkMode ? 'bg-slate-950' : 'bg-white'} overflow-hidden transition-colors duration-300`} style={{ height: '100vh', width: '100vw' }}>
            {/* Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className={`absolute top-0 left-1/4 w-[800px] h-[800px] ${isDarkMode ? 'bg-blue-500/5' : 'bg-blue-500/10'} rounded-full blur-[150px]`} />
                <div className={`absolute bottom-0 right-1/4 w-[600px] h-[600px] ${isDarkMode ? 'bg-amber-500/5' : 'bg-amber-500/10'} rounded-full blur-[150px]`} />
                <div className={`absolute inset-0 ${isDarkMode ? 'opacity-50' : 'opacity-20'}`} 
                    style={{ backgroundImage: `linear-gradient(${isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'} 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
            </div>

            <AnimatePresence mode="wait">
                <motion.div key={currentSlide} initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -50 }} transition={{ duration: 0.5, ease: "easeInOut" }} className="h-full w-full">
                    <SlideComponent isDarkMode={isDarkMode} />
                </motion.div>
            </AnimatePresence>

            {/* Bottom Navigation */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-50">
                {Array.from({ length: totalSlides }).map((_, i) => (
                    <button key={i} onClick={() => goToSlide(i)} className={`transition-all duration-300 rounded-full ${i === currentSlide ? 'w-8 h-2 bg-amber-500' : `w-2 h-2 ${isDarkMode ? 'bg-white/30 hover:bg-white/50' : 'bg-slate-400 hover:bg-slate-600'}`}`} />
                ))}
            </div>

            {/* Side Arrows */}
            {currentSlide > 0 && (
                <button onClick={prevSlide} className={`absolute left-6 top-1/2 -translate-y-1/2 p-3 rounded-full ${controlBgClass} border ${controlBorderClass} transition-all group z-50`}>
                    <ChevronLeft className={`w-6 h-6 ${isDarkMode ? 'text-white/50 group-hover:text-white' : 'text-slate-400 group-hover:text-slate-900'} transition-colors`} />
                </button>
            )}
            {currentSlide < totalSlides - 1 && (
                <button onClick={nextSlide} className={`absolute right-6 top-1/2 -translate-y-1/2 p-3 rounded-full ${controlBgClass} border ${controlBorderClass} transition-all group z-50`}>
                    <ChevronRight className={`w-6 h-6 ${isDarkMode ? 'text-white/50 group-hover:text-white' : 'text-slate-400 group-hover:text-slate-900'} transition-colors`} />
                </button>
            )}

            {/* Top Bar */}
            <div className="absolute top-6 left-8 right-8 flex items-center justify-between z-50">
                <button onClick={goToHome} className="flex items-center gap-3 hover:opacity-80 transition-opacity" title="Back to Home">
                    <img src="https://customer-assets.emergentagent.com/job_everestcloud/artifacts/iuie7tiv_dns-logo-symbol.png" alt="EverestCloud" className="h-10 w-10 object-contain" />
                    <span className={`${isDarkMode ? 'text-white' : 'text-slate-900'} font-bold text-xl tracking-tight`}>Everest<span className="text-amber-500">Cloud</span></span>
                </button>
                <div className="flex items-center gap-3">
                    <span className={`${isDarkMode ? 'text-white/40' : 'text-slate-500'} text-sm font-mono`}>{currentSlide + 1} / {totalSlides}</span>
                    <button onClick={toggleTheme} className={`p-2 rounded-lg ${controlBgClass} border ${controlBorderClass} transition-all`}>
                        {isDarkMode ? <Sun className="w-5 h-5 text-amber-500" /> : <Moon className="w-5 h-5 text-slate-600" />}
                    </button>
                    <button onClick={toggleFullscreen} className={`p-2 rounded-lg ${controlBgClass} border ${controlBorderClass} transition-all`}>
                        {isFullscreen ? <Minimize className={`w-5 h-5 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`} /> : <Maximize className={`w-5 h-5 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`} />}
                    </button>
                    <button onClick={goToHome} className={`p-2 rounded-lg ${controlBgClass} border ${controlBorderClass} transition-all hover:bg-red-500/20 hover:border-red-500/50`}>
                        <X className={`w-5 h-5 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`} />
                    </button>
                </div>
            </div>

            <div className={`absolute bottom-8 right-8 ${isDarkMode ? 'text-white/20' : 'text-slate-400'} text-xs font-mono z-50`}>
                Arrow Keys Navigate | F Fullscreen | ESC Exit
            </div>
        </div>
    );
};

// ==================== HELPER COMPONENTS ====================

const AnimatedCounter = ({ value, suffix = '', isDarkMode }) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
        const num = parseFloat(value);
        const duration = 1500;
        const steps = 40;
        const increment = num / steps;
        let current = 0;
        const timer = setInterval(() => {
            current += increment;
            if (current >= num) { setCount(num); clearInterval(timer); }
            else setCount(Math.floor(current * 10) / 10);
        }, duration / steps);
        return () => clearInterval(timer);
    }, [value]);
    return <span>{count % 1 === 0 ? Math.floor(count) : count.toFixed(1)}{suffix}</span>;
};

const SlideTitle = ({ children, isDarkMode, delay = 0.2 }) => (
    <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} className={`text-5xl md:text-6xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-4 text-center`}>
        {children}
    </motion.h2>
);

const SlideSubtitle = ({ children, isDarkMode, delay = 0.3 }) => (
    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay }} className={`text-xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} text-center mb-14`}>
        {children}
    </motion.p>
);

// ==================== SLIDE 1: WELCOME ====================
const WelcomeSlide = ({ isDarkMode }) => (
    <section className="h-full w-full flex items-center justify-center px-16 relative">
        <div className="absolute inset-0 bg-cover bg-no-repeat opacity-20" style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_everestcloud/artifacts/spue5mbw_backgroundjpeg.jpg')", backgroundPosition: "center 30%" }} />
        <div className="text-center max-w-5xl relative z-10">
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.8 }}>
                <img src="https://customer-assets.emergentagent.com/job_everestcloud/artifacts/iuie7tiv_dns-logo-symbol.png" alt="Digital Network Solution" className="h-28 w-28 mx-auto mb-8 object-contain" />
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }} className={`text-5xl md:text-7xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-6 leading-tight`}>
                Welcome to <span className="text-amber-500">EverestCloud</span>
            </motion.h1>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }} className="mb-8">
                <p className={`text-2xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} max-w-3xl mx-auto leading-relaxed`}>
                    A Sovereign Cloud Platform by <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Digital Network Solution</span>
                </p>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }} 
                className={`inline-flex items-center gap-3 px-6 py-3 rounded-full ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
                <Globe className="text-amber-500 w-5 h-5" />
                <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Nepal's Premier System Integrator & Cloud Service Provider</span>
            </motion.div>
        </div>
    </section>
);

// ==================== SLIDE 2: DNS COMPANY OVERVIEW ====================
const DNSOverviewSlide = ({ isDarkMode }) => {
    const milestones = [
        { num: '01', title: 'Company', desc: 'Founded 18 years ago, we have established a strong presence in the IT industry.', color: 'rose' },
        { num: '02', title: 'Certifications', desc: 'Certified with ISO 9001:2015 and 27001:2013, ensuring quality and security standards.', color: 'teal' },
        { num: '03', title: 'Team Size', desc: 'Our team consists of 180+ professionals, primarily focusing on technical expertise.', color: 'amber' },
        { num: '04', title: 'Service Type', desc: 'We operate as a System Integrator and Cloud Service Provider, delivering comprehensive IT services.', color: 'cyan' },
        { num: '05', title: 'Technical Expertise', desc: 'The majority of our workforce are technical staff, enhancing service delivery and innovation.', color: 'emerald' },
        { num: '06', title: 'Company Mission', desc: 'We aim to deliver high-quality IT solutions that meet the evolving needs of our clients.', color: 'blue' },
    ];

    const colorMap = {
        rose: { bg: 'bg-rose-500', text: 'text-rose-500', border: 'border-rose-500/30' },
        teal: { bg: 'bg-teal-500', text: 'text-teal-500', border: 'border-teal-500/30' },
        amber: { bg: 'bg-amber-500', text: 'text-amber-500', border: 'border-amber-500/30' },
        cyan: { bg: 'bg-cyan-500', text: 'text-cyan-500', border: 'border-cyan-500/30' },
        emerald: { bg: 'bg-emerald-500', text: 'text-emerald-500', border: 'border-emerald-500/30' },
        blue: { bg: 'bg-blue-500', text: 'text-blue-500', border: 'border-blue-500/30' },
    };

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className={`text-4xl md:text-5xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-2`}>
                    <span className="text-amber-500">Digital Network Solution</span> Company Overview
                </motion.h2>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                    className={`text-lg ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mb-10`}>
                    An Insight into our Journey
                </motion.p>
                
                <div className="grid grid-cols-2 gap-x-12 gap-y-6">
                    {milestones.map((item, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.1 }}
                            className="flex items-start gap-4">
                            <div className={`${colorMap[item.color].bg} text-white font-bold text-sm px-3 py-1 rounded-md shrink-0`}>
                                {item.num}
                            </div>
                            <div>
                                <h3 className={`font-bold text-lg mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{item.title}</h3>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} leading-relaxed`}>{item.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
                    className={`mt-10 flex justify-center gap-8`}>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
                        <Shield className="w-4 h-4 text-emerald-500" />
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>ISO 9001:2015</span>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isDarkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
                        <Lock className="w-4 h-4 text-blue-500" />
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>ISO 27001:2013</span>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                        <Users className="w-4 h-4 text-amber-500" />
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>180+ Team Members</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

// ==================== SLIDE 3: DNS SERVICES ====================
const DNSServicesSlide = ({ isDarkMode }) => {
    const services = [
        { icon: Wand2, title: 'Consulting & Digital Transformation', desc: 'Strategic guidance for navigating the digital landscape.' },
        { icon: Cloud, title: 'Private Cloud & Hybrid Services', desc: 'Scalable, secure, and managed cloud infrastructure.' },
        { icon: Bot, title: 'AI, AIOps & Automation', desc: 'Intelligent operations for the modern enterprise.' },
        { icon: Shield, title: 'SIEM, SOC & Cybersecurity', desc: 'Comprehensive threat detection and response.' },
        { icon: Network, title: 'Data Center Network & Security', desc: 'High-performance networking for critical workloads.' },
        { icon: Code2, title: 'Kubernetes & Modernization', desc: 'Containerization for cloud-native agility.' },
        { icon: HardDrive, title: 'Infrastructure Backup & Recovery', desc: 'Ironclad data protection strategies.' },
        { icon: Briefcase, title: 'Digital Workplace & Analytics', desc: 'Empowering the hybrid workforce.' },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Enterprise-Grade <span className="text-amber-500">Ecosystem</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>End-to-end capabilities designed for mission-critical environments.</SlideSubtitle>
                
                <div className="grid grid-cols-4 gap-5">
                    {services.map((service, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.08 }}
                            className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10 hover:border-blue-500/30' : 'bg-slate-50 border-slate-200 hover:border-blue-300'} transition-all group`}>
                            <div className={`w-10 h-10 rounded-xl ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                                <service.icon className="w-5 h-5 text-blue-500" />
                            </div>
                            <h3 className={`font-bold text-sm mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'} leading-tight`}>{service.title}</h3>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} leading-relaxed`}>{service.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 4: DNS PARTNERS ====================
const PartnersSlide = ({ isDarkMode }) => {
    const partners = [
        { name: 'Nutanix', tier: 'PREMIER PARTNER', desc: 'Hyperconverged infrastructure delivering cloud-like simplicity for on-premises environments.', tags: ['HCI Platform', 'Cloud Management'] },
        { name: 'Cisco', tier: 'PREMIER PARTNER', desc: 'Networking, security, and collaboration solutions. Certified engineers delivering enterprise-grade infrastructure.', tags: ['Enterprise Networking', 'Security'] },
        { name: 'Fortinet', tier: 'PARTNER', desc: 'Security Fabric covering network security, SD-WAN, and secure access solutions.', tags: ['FortiGate Firewalls', 'SD-WAN'] },
        { name: 'Palo Alto Networks', tier: 'PARTNER', desc: 'Next-generation cybersecurity solutions including firewalls, cloud security, and AI-driven threat detection.', tags: ['Next-Gen Firewalls', 'Cortex XDR'] },
        { name: 'F5 Networks', tier: 'PARTNER', desc: 'Application delivery and security solutions ensuring high performance and availability.', tags: ['Load Balancing', 'WAF'] },
        { name: 'Juniper Networks', tier: 'PARTNER', desc: 'Routing, switching, and security solutions for service provider and enterprise networks.', tags: ['Routing', 'AI-Driven Operations'] },
        { name: 'Arista Networks', tier: 'PARTNER', desc: 'Cloud networking solutions for large-scale data center and cloud environments.', tags: ['Data Center', 'Cloud Networking'] },
        { name: 'Commvault', tier: 'PARTNER', desc: 'Enterprise backup, recovery, and data management protecting critical data across all environments.', tags: ['Data Protection', 'Disaster Recovery'] },
        { name: 'HYCU', tier: 'PARTNER', desc: 'Purpose-built backup and recovery solutions for multi-cloud and hyperconverged environments.', tags: ['Multi-Cloud Backup', 'SaaS Protection'] },
        { name: 'CyberArk', tier: 'PARTNER', desc: 'Identity security and privileged access management protecting against advanced threats.', tags: ['PAM', 'Identity Security'] },
        { name: 'LogRhythm', tier: 'PARTNER', desc: 'Security intelligence and analytics platform for comprehensive threat detection and response.', tags: ['SIEM', 'Threat Detection'] },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-12 py-6">
            <div className="max-w-7xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Partner <span className="text-amber-500">Portfolio</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>World-class technology partnerships powering enterprise solutions</SlideSubtitle>
                
                <div className="grid grid-cols-4 gap-4">
                    {partners.slice(0, 8).map((partner, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.05 }}
                            className={`p-4 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-slate-200 hover:border-slate-300'} transition-all`}>
                            <h3 className={`font-bold text-base mb-1 ${partner.tier === 'PREMIER PARTNER' ? 'text-amber-500' : (isDarkMode ? 'text-white' : 'text-slate-900')}`}>
                                {partner.name}
                            </h3>
                            <span className={`text-[10px] font-bold tracking-wider ${partner.tier === 'PREMIER PARTNER' ? 'text-amber-500/70' : (isDarkMode ? 'text-slate-500' : 'text-slate-400')}`}>
                                {partner.tier}
                            </span>
                            <p className={`text-xs mt-2 mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} line-clamp-2`}>{partner.desc}</p>
                            <div className="flex flex-wrap gap-1">
                                {partner.tags.map((tag, j) => (
                                    <span key={j} className={`text-[10px] px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
                
                <div className="grid grid-cols-3 gap-4 mt-4 max-w-4xl mx-auto">
                    {partners.slice(8).map((partner, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 + i * 0.05 }}
                            className={`p-4 rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-slate-200 hover:border-slate-300'} transition-all`}>
                            <h3 className={`font-bold text-base mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{partner.name}</h3>
                            <span className={`text-[10px] font-bold tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{partner.tier}</span>
                            <p className={`text-xs mt-2 mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} line-clamp-2`}>{partner.desc}</p>
                            <div className="flex flex-wrap gap-1">
                                {partner.tags.map((tag, j) => (
                                    <span key={j} className={`text-[10px] px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 4: HERO ====================
const HeroSlide = ({ isDarkMode }) => (
    <section className="h-full w-full flex items-center justify-center px-16 relative">
        <div className="absolute inset-0 bg-cover bg-no-repeat opacity-30" style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_everestcloud/artifacts/spue5mbw_backgroundjpeg.jpg')", backgroundPosition: "center 30%" }} />
        <div className="text-center max-w-5xl relative z-10">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.8 }}>
                <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-8">
                    <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                    <span className="text-amber-500 text-sm font-bold tracking-[0.15em] uppercase">Nepal's Sovereign Cloud</span>
                </div>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }} className={`text-6xl md:text-8xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-8 leading-tight`}>
                Sovereign by Design.<br /><span className="text-amber-500">Resilient by Architecture.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }} className={`text-2xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} max-w-3xl mx-auto leading-relaxed`}>
                Enterprise cloud infrastructure built entirely within Nepal. Your data never leaves the country.
            </motion.p>
        </div>
    </section>
);

// ==================== SLIDE 5: BY THE NUMBERS ====================
const ByTheNumbersSlide = ({ isDarkMode }) => {
    const metrics = [
        { value: '99.9', suffix: '%', label: 'Uptime SLA', icon: Activity, color: 'emerald' },
        { value: '3', suffix: '', label: 'Availability Zones', icon: MapPin, color: 'amber' },
        { value: '40', suffix: '+', label: 'Compute Nodes', icon: Server, color: 'blue' },
        { value: '2.5', suffix: ' PB', label: 'NVMe Storage', icon: Database, color: 'purple' },
        { value: '240', suffix: ' Gbps', label: 'Backplane Capacity', icon: Network, color: 'cyan' },
        { value: '8', suffix: ' ms', label: 'Local Latency', icon: Zap, color: 'rose' },
    ];
    const colorMap = { emerald: 'text-emerald-500', amber: 'text-amber-500', blue: 'text-blue-500', purple: 'text-purple-500', cyan: 'text-cyan-500', rose: 'text-rose-500' };
    const bgMap = { emerald: 'bg-emerald-500/10', amber: 'bg-amber-500/10', blue: 'bg-blue-500/10', purple: 'bg-purple-500/10', cyan: 'bg-cyan-500/10', rose: 'bg-rose-500/10' };
    const borderMap = { emerald: 'border-emerald-500/20', amber: 'border-amber-500/20', blue: 'border-blue-500/20', purple: 'border-purple-500/20', cyan: 'border-cyan-500/20', rose: 'border-rose-500/20' };

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>By the <span className="text-amber-500">Numbers</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Infrastructure that speaks for itself</SlideSubtitle>
                <div className="grid grid-cols-3 gap-6">
                    {metrics.map((m, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
                            className={`p-6 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} relative overflow-hidden group hover:scale-[1.02] transition-transform`}>
                            <div className={`absolute -top-6 -right-6 w-24 h-24 rounded-full ${bgMap[m.color]} blur-2xl opacity-50 group-hover:opacity-80 transition-opacity`} />
                            <div className={`w-12 h-12 rounded-xl ${bgMap[m.color]} border ${borderMap[m.color]} flex items-center justify-center mb-4`}>
                                <m.icon className={`w-6 h-6 ${colorMap[m.color]}`} />
                            </div>
                            <div className={`text-4xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-1`}>
                                <AnimatedCounter value={m.value} suffix={m.suffix} isDarkMode={isDarkMode} />
                            </div>
                            <p className={`text-sm font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{m.label}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 6: WHY EVERESTCLOUD ====================
const WhyEverestCloudSlide = ({ isDarkMode }) => {
    const points = [
        { icon: Shield, title: "Data Sovereignty", desc: "100% Nepal-hosted infrastructure under local jurisdiction. No hyperscaler dependencies.", color: 'amber' },
        { icon: Lock, title: "Zero Foreign Access", desc: "No offshore data exposure. Compliant with NRB and national data policies.", color: 'red' },
        { icon: Activity, title: "Business Continuity", desc: "Multi-site architecture with automated failover. RPO=0, RTO<15 min.", color: 'emerald' },
        { icon: Zap, title: "Local Performance", desc: "Sub-10ms latency via NPIX direct peering. 5x faster than hyperscalers.", color: 'blue' },
    ];
    const colorMap = { amber: 'text-amber-500 bg-amber-500/10 border-amber-500/20', red: 'text-red-500 bg-red-500/10 border-red-500/20', emerald: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20', blue: 'text-blue-500 bg-blue-500/10 border-blue-500/20' };

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Why <span className="text-amber-500">EverestCloud</span>?</SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Built for enterprises that demand control</SlideSubtitle>
                <div className="grid grid-cols-2 gap-6">
                    {points.map((p, i) => {
                        const [textC, bgC, borderC] = colorMap[p.color].split(' ');
                        return (
                            <motion.div key={i} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.15 }}
                                className={`p-7 rounded-2xl ${isDarkMode ? 'bg-white/5 border-white/10 hover:border-white/20' : 'bg-slate-50 border-slate-200 hover:border-slate-300'} border transition-all group`}>
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-xl ${bgC} border ${borderC} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                                        <p.icon className={`w-6 h-6 ${textC}`} />
                                    </div>
                                    <div>
                                        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-1`}>{p.title}</h3>
                                        <p className={`${isDarkMode ? 'text-slate-400' : 'text-slate-600'} leading-relaxed`}>{p.desc}</p>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 7: TARGET INDUSTRIES ====================
const IndustriesSlide = ({ isDarkMode }) => {
    const industries = [
        { icon: Landmark, name: "Banking & Finance", desc: "NRB-compliant infrastructure for core banking, digital payments, and financial analytics.", stat: "6 of top 10 banks" },
        { icon: Building2, name: "Government", desc: "Sovereign infrastructure for e-governance, national databases, and citizen services.", stat: "Data never leaves Nepal" },
        { icon: Radio, name: "Telecom", desc: "High-throughput compute for billing systems, subscriber management, and 5G readiness.", stat: "240 Gbps backbone" },
        { icon: Briefcase, name: "Enterprise", desc: "Dedicated resources for ERP, CRM, and mission-critical business applications.", stat: "99.9% SLA" },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Built for <span className="text-amber-500">Regulated Industries</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Trusted by Nepal's most demanding organizations</SlideSubtitle>
                <div className="grid grid-cols-2 gap-6">
                    {industries.map((ind, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.15 }}
                            className={`p-6 rounded-2xl ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} border group hover:scale-[1.02] transition-all`}>
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-14 h-14 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <ind.icon className="w-7 h-7 text-amber-500" />
                                </div>
                                <div>
                                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{ind.name}</h3>
                                    <span className="text-amber-500 text-xs font-bold uppercase tracking-wider">{ind.stat}</span>
                                </div>
                            </div>
                            <p className={`${isDarkMode ? 'text-slate-400' : 'text-slate-600'} leading-relaxed`}>{ind.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 8: AVAILABILITY ZONES ====================
const AvailabilityZonesSlide = ({ isDarkMode }) => {
    const zones = [
        { name: "KATHMANDU", code: "AZ-1", role: "Primary DC", color: "emerald", status: "Active", icon: Server },
        { name: "LALITPUR", code: "AZ-2", role: "Near DR", color: "amber", status: "Sync Replica", icon: RefreshCw },
        { name: "BUTWAL", code: "AZ-3", role: "Far DR", color: "blue", status: "Async Replica", icon: Database },
    ];
    const colorStyles = {
        emerald: { border: isDarkMode ? 'border-emerald-500/50' : 'border-emerald-500', bg: isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50', text: isDarkMode ? 'text-emerald-400' : 'text-emerald-600', dot: 'bg-emerald-500' },
        amber: { border: isDarkMode ? 'border-amber-500/50' : 'border-amber-500', bg: isDarkMode ? 'bg-amber-500/10' : 'bg-amber-50', text: isDarkMode ? 'text-amber-400' : 'text-amber-600', dot: 'bg-amber-500' },
        blue: { border: isDarkMode ? 'border-blue-500/50' : 'border-blue-500', bg: isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50', text: isDarkMode ? 'text-blue-400' : 'text-blue-600', dot: 'bg-blue-500' },
    };

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Three <span className="text-amber-500">Availability Zones</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Geographically separated for true disaster resilience</SlideSubtitle>
                <div className="grid grid-cols-3 gap-8">
                    {zones.map((zone, i) => {
                        const s = colorStyles[zone.color];
                        return (
                            <motion.div key={i} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.2 }}
                                className={`p-8 rounded-2xl border-2 ${s.border} ${s.bg} relative overflow-hidden`}>
                                <div className="flex items-center gap-2 mb-4">
                                    <div className={`w-3 h-3 rounded-full ${s.dot} ${zone.color === 'emerald' ? 'animate-pulse' : ''}`} />
                                    <span className={`text-sm font-bold uppercase tracking-wider ${s.text}`}>{zone.status}</span>
                                </div>
                                <h3 className={`text-3xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-1`}>{zone.name}</h3>
                                <p className={`text-lg font-bold ${s.text} mb-3`}>{zone.code}</p>
                                <div className="flex items-center gap-2">
                                    <zone.icon className={`w-4 h-4 ${s.text}`} />
                                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>{zone.role}</p>
                                </div>
                                <div className={`absolute -bottom-8 -right-8 w-32 h-32 rounded-full opacity-20 ${s.dot} blur-2xl`} />
                            </motion.div>
                        );
                    })}
                </div>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-10 text-center">
                    <div className={`inline-flex items-center gap-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-600'} text-sm`}>
                        <span className="flex items-center gap-2"><span className="w-2 h-2 bg-amber-500 rounded-full" /> RPO = 0 (Sync)</span>
                        <span className="flex items-center gap-2"><span className="w-2 h-2 bg-blue-500 rounded-full" /> RTO &lt; 15 min</span>
                        <span className="flex items-center gap-2"><MapPin className="w-4 h-4" /> 250km+ Separation</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

// ==================== SLIDE 9: ARCHITECTURE ====================
const ArchitectureSlide = ({ isDarkMode }) => (
    <section className="h-full w-full flex items-center justify-center px-16">
        <div className="max-w-6xl w-full">
            <SlideTitle isDarkMode={isDarkMode}>Multi-AZ <span className="text-amber-500">Architecture</span></SlideTitle>
            <SlideSubtitle isDarkMode={isDarkMode}>Designed for zero-downtime operations</SlideSubtitle>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }}>
                <div className={`${isDarkMode ? 'bg-slate-900/50 border-white/10' : 'bg-slate-50 border-slate-200'} rounded-3xl border p-10`}>
                    <svg viewBox="0 0 800 380" className="w-full h-auto">
                        <text x="400" y="25" textAnchor="middle" fill={isDarkMode ? '#94a3b8' : '#64748b'} fontSize="13" fontWeight="bold">INTERNET TRAFFIC</text>
                        <rect x="325" y="40" width="150" height="36" rx="8" fill={isDarkMode ? '#1e293b' : '#e2e8f0'} stroke={isDarkMode ? '#334155' : '#cbd5e1'} strokeWidth="2" />
                        <text x="400" y="63" textAnchor="middle" fill={isDarkMode ? '#fff' : '#1e293b'} fontSize="13" fontWeight="bold">GSLB / DNS</text>
                        <line x1="400" y1="76" x2="150" y2="140" stroke="#10b981" strokeWidth="2" />
                        <line x1="400" y1="76" x2="400" y2="140" stroke="#f59e0b" strokeWidth="2" />
                        <line x1="400" y1="76" x2="650" y2="140" stroke="#3b82f6" strokeWidth="2" />
                        {[{ x: 50, color: '#10b981', name: 'KATHMANDU', az: 'AZ-1', role: 'Primary DC', items: ['Compute + Storage', 'Database Cluster'] },
                          { x: 300, color: '#f59e0b', name: 'LALITPUR', az: 'AZ-2', role: 'Near DR', items: ['Sync Replica', 'Active Standby'] },
                          { x: 550, color: '#3b82f6', name: 'BUTWAL', az: 'AZ-3', role: 'Far DR', items: ['Async Replica', 'Cold Standby'] }
                        ].map((z, i) => (
                            <g key={i}>
                                <rect x={z.x} y="140" width="200" height="200" rx="12" fill={isDarkMode ? `${z.color}15` : `${z.color}10`} stroke={z.color} strokeWidth="2" />
                                <text x={z.x + 100} y="168" textAnchor="middle" fill={z.color} fontSize="11" fontWeight="bold">{z.name}</text>
                                <text x={z.x + 100} y="190" textAnchor="middle" fill={isDarkMode ? '#fff' : '#1e293b'} fontSize="16" fontWeight="bold">{z.az}</text>
                                <text x={z.x + 100} y="208" textAnchor="middle" fill={z.color} fontSize="10">{z.role}</text>
                                {z.items.map((item, j) => (
                                    <g key={j}><rect x={z.x + 30} y={240 + j * 38} width="140" height="28" rx="4" fill={isDarkMode ? '#1e293b' : '#e2e8f0'} />
                                    <text x={z.x + 100} y={258 + j * 38} textAnchor="middle" fill={isDarkMode ? '#94a3b8' : '#64748b'} fontSize="10">{item}</text></g>
                                ))}
                            </g>
                        ))}
                        <line x1="250" y1="260" x2="300" y2="260" stroke="#f59e0b" strokeWidth="2" strokeDasharray="4,4" />
                        <text x="275" y="252" textAnchor="middle" fill="#f59e0b" fontSize="9">SYNC</text>
                        <line x1="500" y1="290" x2="550" y2="290" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4,4" />
                        <text x="525" y="282" textAnchor="middle" fill="#3b82f6" fontSize="9">ASYNC</text>
                    </svg>
                </div>
            </motion.div>
        </div>
    </section>
);

// ==================== SLIDE 10: INFRASTRUCTURE SCALE ====================
const InfraScaleSlide = ({ isDarkMode }) => {
    const zones = [
        { name: 'KATHMANDU', cpu: 85, storage: 78, ram: 72, color: 'emerald' },
        { name: 'LALITPUR', cpu: 65, storage: 60, ram: 55, color: 'amber' },
        { name: 'BUTWAL', cpu: 45, storage: 40, ram: 38, color: 'blue' },
    ];
    const barColors = { emerald: 'bg-emerald-500', amber: 'bg-amber-500', blue: 'bg-blue-500' };

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Infrastructure <span className="text-amber-500">Scale</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Resource distribution across availability zones</SlideSubtitle>
                <div className="grid grid-cols-3 gap-6 mb-8">
                    {[{ label: 'Compute Nodes', val: '40+', icon: Cpu }, { label: 'NVMe Storage', val: '2.5+ PB', icon: Database }, { label: 'Network Backbone', val: '240 Gbps', icon: Network }].map((s, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1 }}
                            className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} border text-center`}>
                            <s.icon className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                            <div className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{s.val}</div>
                            <p className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{s.label}</p>
                        </motion.div>
                    ))}
                </div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                    className={`p-8 rounded-2xl ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} border`}>
                    <div className="flex items-center justify-between mb-6">
                        <h4 className={`text-sm font-bold uppercase tracking-wider ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Resource Heatmap by Zone</h4>
                        <div className="flex gap-4 text-xs">
                            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" /> Compute</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-sm" /> Storage</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded-sm" /> Memory</span>
                        </div>
                    </div>
                    <div className="space-y-6">
                        {zones.map((zone, i) => (
                            <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 + i * 0.15 }}>
                                <div className="flex justify-between mb-2">
                                    <span className={`text-xs font-black tracking-widest ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{zone.name}</span>
                                    <span className={`text-xs font-mono ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>AZ-{i + 1}</span>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    {[zone.cpu, zone.storage, zone.ram].map((val, j) => (
                                        <div key={j} className={`h-6 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                                            <motion.div initial={{ width: 0 }} animate={{ width: `${val}%` }} transition={{ duration: 1.2, delay: 0.8 + i * 0.15 + j * 0.1 }}
                                                className={`h-full rounded-full ${j === 0 ? 'bg-emerald-500' : j === 1 ? 'bg-blue-500' : 'bg-amber-500'}`} />
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

// ==================== SLIDE 11: PERFORMANCE ====================
const PerformanceSlide = ({ isDarkMode }) => {
    const providers = [
        { name: 'EverestCloud (Local)', latency: 8, display: '<10 ms', color: 'emerald', highlight: true },
        { name: 'AWS (Mumbai)', latency: 45, display: '45 ms', color: 'slate' },
        { name: 'Azure (Central India)', latency: 52, display: '52 ms', color: 'slate' },
        { name: 'GCP (Singapore)', latency: 65, display: '65 ms', color: 'slate' },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>The <span className="text-amber-500">Latency</span> Advantage</SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Why local infrastructure matters for performance</SlideSubtitle>
                <div className="grid grid-cols-2 gap-8">
                    <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
                        className={`p-8 rounded-2xl ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} border`}>
                        <div className="flex items-center gap-3 mb-8">
                            <Zap className="text-emerald-500 w-6 h-6" />
                            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Round-Trip Latency (ms)</h3>
                        </div>
                        <div className="space-y-5">
                            {providers.map((p, i) => (
                                <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 + i * 0.15 }}>
                                    <div className="flex justify-between text-sm font-medium mb-1.5">
                                        <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>{p.name}</span>
                                        <span className={p.highlight ? 'text-emerald-500 font-bold' : (isDarkMode ? 'text-slate-500' : 'text-slate-400')}>{p.display}</span>
                                    </div>
                                    <div className={`w-full h-4 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                                        <motion.div initial={{ width: 0 }} animate={{ width: `${(p.latency / 70) * 100}%` }} transition={{ duration: 1, delay: 0.6 + i * 0.15 }}
                                            className={`h-full rounded-full ${p.highlight ? 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.5)]' : isDarkMode ? 'bg-slate-600' : 'bg-slate-400'}`} />
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                        <p className={`mt-6 text-xs italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>* Measured from ISP backbones in Kathmandu</p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }} className="space-y-6">
                        {[{ value: '5x', label: 'Faster than nearest hyperscaler', desc: 'Direct NPIX peering eliminates international hops' },
                          { value: '2-3', label: 'Hop count (vs 12-15 for international)', desc: 'Traffic stays within Nepal\'s network fabric' },
                          { value: '0ms', label: 'Jitter on local connections', desc: 'Consistent, predictable performance' }
                        ].map((stat, i) => (
                            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + i * 0.15 }}
                                className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} border`}>
                                <div className="flex items-baseline gap-3 mb-1">
                                    <span className="text-3xl font-black text-emerald-500">{stat.value}</span>
                                    <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{stat.label}</span>
                                </div>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{stat.desc}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 12: NETWORK PEERING ====================
const NetworkPeeringSlide = ({ isDarkMode }) => (
    <section className="h-full w-full flex items-center justify-center px-16">
        <div className="max-w-6xl w-full">
            <SlideTitle isDarkMode={isDarkMode}>Direct <span className="text-amber-500">NPIX Peering</span></SlideTitle>
            <SlideSubtitle isDarkMode={isDarkMode}>Local traffic stays local. Zero international hops.</SlideSubtitle>
            <div className="grid grid-cols-2 gap-8">
                {/* Local Path */}
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                    className={`p-8 rounded-2xl border-2 ${isDarkMode ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-emerald-500/50 bg-emerald-50'}`}>
                    <div className="flex items-center gap-2 text-sm font-black text-emerald-500 uppercase mb-6"><Zap size={16} /> EverestCloud Path</div>
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex flex-col items-center gap-2">
                            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}><Users className={`${isDarkMode ? 'text-white' : 'text-slate-700'}`} size={24} /></div>
                            <span className={`text-xs font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>User in Nepal</span>
                        </div>
                        <div className="flex-1 mx-4 relative h-8 flex items-center">
                            <div className="h-[2px] w-full bg-emerald-500/30" />
                            <motion.div animate={{ left: ['0%', '100%'] }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }} className="absolute w-3 h-3 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
                        </div>
                        <div className="flex flex-col items-center gap-2 text-center">
                            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}><Globe className="text-emerald-500" size={24} /></div>
                            <span className="text-xs font-bold text-emerald-500">NPIX</span>
                        </div>
                        <div className="flex-1 mx-4 relative h-8 flex items-center">
                            <div className="h-[2px] w-full bg-emerald-500/30" />
                            <motion.div animate={{ left: ['0%', '100%'] }} transition={{ duration: 1, repeat: Infinity, ease: "linear", delay: 0.5 }} className="absolute w-3 h-3 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}><Server className="text-emerald-500" size={24} /></div>
                            <span className="text-xs font-bold text-emerald-500">EverestCloud</span>
                        </div>
                    </div>
                    <div className="flex items-center justify-center gap-6 text-sm">
                        <span className="text-emerald-500 font-bold">2-3 hops</span>
                        <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>|</span>
                        <span className="text-emerald-500 font-bold">&lt;10ms latency</span>
                        <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>|</span>
                        <span className="text-emerald-500 font-bold">0ms jitter</span>
                    </div>
                </motion.div>

                {/* International Path */}
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
                    className={`p-8 rounded-2xl border-2 ${isDarkMode ? 'border-slate-700 bg-slate-900/50' : 'border-slate-300 bg-slate-100'}`}>
                    <div className={`flex items-center gap-2 text-sm font-black uppercase mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}><Globe size={16} /> Hyperscaler Path</div>
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex flex-col items-center gap-2">
                            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}><Users className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} size={24} /></div>
                            <span className={`text-xs font-bold ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>User in Nepal</span>
                        </div>
                        <div className="flex-1 mx-2 relative h-8 flex items-center">
                            <div className={`h-[2px] w-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-300'}`} />
                            <motion.div animate={{ left: ['0%', '100%'] }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }} className={`absolute w-3 h-3 rounded-full ${isDarkMode ? 'bg-slate-500' : 'bg-slate-400'}`} />
                        </div>
                        <div className="flex flex-col items-center gap-2 text-center">
                            <div className={`p-2 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}><ArrowUpRight className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} size={18} /></div>
                            <span className={`text-[10px] font-bold ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Undersea Cable</span>
                        </div>
                        <div className="flex-1 mx-2 relative h-8 flex items-center">
                            <div className={`h-[2px] w-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-300'}`} />
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}><Cloud className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} size={24} /></div>
                            <span className={`text-xs font-bold ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Mumbai / SG</span>
                        </div>
                    </div>
                    <div className={`flex items-center justify-center gap-6 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        <span className="font-bold">12-15 hops</span><span>|</span>
                        <span className="font-bold">45-65ms latency</span><span>|</span>
                        <span className="font-bold">Variable jitter</span>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
);

// ==================== SLIDE 13: PRODUCTS ====================
const ProductsSlide = ({ isDarkMode }) => {
    const products = [
        { icon: Server, name: "Compute", desc: "Virtual Machines & Containers", color: 'emerald' },
        { icon: Network, name: "Network", desc: "Software-Defined Networking", color: 'blue' },
        { icon: Database, name: "Storage", desc: "Block, Object & File Storage", color: 'purple' },
        { icon: Shield, name: "DR & Backup", desc: "Automated Recovery", color: 'amber' },
        { icon: Cpu, name: "GPU / AI", desc: "ML Infrastructure", color: 'rose' },
        { icon: Lock, name: "Security", desc: "Managed SOC & PAM", color: 'cyan' },
    ];
    const colorMap = { emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500', blue: 'bg-blue-500/10 border-blue-500/20 text-blue-500', purple: 'bg-purple-500/10 border-purple-500/20 text-purple-500', amber: 'bg-amber-500/10 border-amber-500/20 text-amber-500', rose: 'bg-rose-500/10 border-rose-500/20 text-rose-500', cyan: 'bg-cyan-500/10 border-cyan-500/20 text-cyan-500' };

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Platform <span className="text-amber-500">Services</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Enterprise-grade infrastructure, fully managed</SlideSubtitle>
                <div className="grid grid-cols-3 gap-6">
                    {products.map((p, i) => {
                        const [bg, border, text] = colorMap[p.color].split(' ');
                        return (
                            <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 + i * 0.1 }}
                                className={`p-6 rounded-xl ${isDarkMode ? 'bg-white/5 border-white/10 hover:border-white/20' : 'bg-slate-50 border-slate-200 hover:border-slate-300'} border transition-all group text-center`}>
                                <div className={`w-16 h-16 rounded-2xl ${bg} border ${border} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                                    <p.icon className={`w-8 h-8 ${text}`} />
                                </div>
                                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-1`}>{p.name}</h3>
                                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>{p.desc}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 14: SECURITY ====================
const SecuritySlide = ({ isDarkMode }) => {
    const features = [
        { title: "Tenant Isolation", desc: "Dedicated compute & network per customer", icon: Layers },
        { title: "Privileged Access Mgmt", desc: "Just-in-time access with full audit trail", icon: Lock },
        { title: "Multi-Factor Auth", desc: "Hardware token & biometric support", icon: Shield },
        { title: "Encryption Everywhere", desc: "AES-256 at rest & in transit, CMK support", icon: Eye },
        { title: "Immutable Audit Logs", desc: "Tamper-proof activity records", icon: BarChart3 },
        { title: "Compliance Ready", desc: "NRB, ISO 27001, SOC 2 aligned", icon: CheckCircle2 },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 mb-5">
                        <Shield className="w-8 h-8 text-amber-500" />
                    </div>
                    <h2 className={`text-5xl md:text-6xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-3`}>Security & Compliance</h2>
                    <p className={`text-xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Enterprise-grade protection, regulator-ready</p>
                </motion.div>
                <div className="grid grid-cols-3 gap-5">
                    {features.map((f, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.08 }}
                            className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} border group hover:scale-[1.02] transition-transform`}>
                            <div className="flex items-center gap-3 mb-2">
                                <f.icon className="w-5 h-5 text-emerald-500" />
                                <h3 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{f.title}</h3>
                            </div>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{f.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 15: OPERATIONS ====================
const OperationsSlide = ({ isDarkMode }) => (
    <section className="h-full w-full flex items-center justify-center px-16">
        <div className="max-w-6xl w-full">
            <SlideTitle isDarkMode={isDarkMode}>24/7 <span className="text-amber-500">Operations</span></SlideTitle>
            <SlideSubtitle isDarkMode={isDarkMode}>Low-human, high-automation infrastructure management</SlideSubtitle>
            <div className="grid grid-cols-2 gap-8">
                <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
                    className={`p-8 rounded-2xl ${isDarkMode ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'} border`}>
                    <div className="flex items-center gap-4 mb-6">
                        <div className={`w-14 h-14 rounded-xl ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'} flex items-center justify-center`}>
                            <Monitor className="w-7 h-7 text-emerald-500" />
                        </div>
                        <div>
                            <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>NOC</h3>
                            <p className={isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}>Network Operations</p>
                        </div>
                    </div>
                    <ul className={`space-y-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {['Real-time infrastructure monitoring', 'Automated incident detection & alerting', 'Proactive capacity management'].map((item, j) => (
                            <li key={j} className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" /> {item}</li>
                        ))}
                    </ul>
                </motion.div>

                <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.55 }}
                    className={`p-8 rounded-2xl ${isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'} border`}>
                    <div className="flex items-center gap-4 mb-6">
                        <div className={`w-14 h-14 rounded-xl ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'} flex items-center justify-center`}>
                            <Shield className="w-7 h-7 text-red-500" />
                        </div>
                        <div>
                            <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>SOC</h3>
                            <p className={isDarkMode ? 'text-red-400' : 'text-red-600'}>Security Operations</p>
                        </div>
                    </div>
                    <ul className={`space-y-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {['Threat detection & response (24/7)', 'Security event correlation (SIEM)', 'Incident forensics & reporting'].map((item, j) => (
                            <li key={j} className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-red-500 flex-shrink-0" /> {item}</li>
                        ))}
                    </ul>
                </motion.div>
            </div>
        </div>
    </section>
);

// ==================== SLIDE 16: SLA GUARANTEES ====================
const SLASlide = ({ isDarkMode }) => {
    const tiers = [
        { name: 'Platinum', p1Resp: '15 min', p1Res: '4 hrs', p2Resp: '30 min', p2Res: '8 hrs', color: 'amber', uptime: '99.9%' },
        { name: 'Gold', p1Resp: '30 min', p1Res: '8 hrs', p2Resp: '1 hr', p2Res: '16 hrs', color: 'yellow', uptime: '99.8%' },
        { name: 'Silver', p1Resp: '1 hr', p1Res: '16 hrs', p2Resp: '2 hrs', p2Res: '24 hrs', color: 'slate', uptime: '99.5%' },
        { name: 'Bronze', p1Resp: '2 hrs', p1Res: '24 hrs', p2Resp: '4 hrs', p2Res: '48 hrs', color: 'orange', uptime: '99%' },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>SLA <span className="text-amber-500">Guarantees</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Tiered service commitments backed by financial penalties</SlideSubtitle>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                    className={`rounded-2xl overflow-hidden border ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                    <table className="w-full">
                        <thead>
                            <tr className={isDarkMode ? 'bg-white/5' : 'bg-slate-100'}>
                                <th className={`text-left py-4 px-6 text-sm font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Tier</th>
                                <th className={`text-center py-4 px-4 text-sm font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Uptime</th>
                                <th className={`text-center py-4 px-4 text-sm font-bold uppercase tracking-wider text-red-500`}>P1 Response</th>
                                <th className={`text-center py-4 px-4 text-sm font-bold uppercase tracking-wider text-red-500`}>P1 Resolution</th>
                                <th className={`text-center py-4 px-4 text-sm font-bold uppercase tracking-wider text-amber-500`}>P2 Response</th>
                                <th className={`text-center py-4 px-4 text-sm font-bold uppercase tracking-wider text-amber-500`}>P2 Resolution</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tiers.map((tier, i) => (
                                <motion.tr key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 + i * 0.1 }}
                                    className={`${isDarkMode ? 'border-white/5 hover:bg-white/5' : 'border-slate-100 hover:bg-slate-50'} border-t transition-colors`}>
                                    <td className="py-4 px-6">
                                        <span className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{tier.name}</span>
                                    </td>
                                    <td className="py-4 px-4 text-center">
                                        <span className="text-emerald-500 font-bold">{tier.uptime}</span>
                                    </td>
                                    <td className={`py-4 px-4 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{tier.p1Resp}</td>
                                    <td className={`py-4 px-4 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{tier.p1Res}</td>
                                    <td className={`py-4 px-4 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{tier.p2Resp}</td>
                                    <td className={`py-4 px-4 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{tier.p2Res}</td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </motion.div>
            </div>
        </section>
    );
};

// ==================== SLIDE 17: GREEN ENERGY ====================
const GreenEnergySlide = ({ isDarkMode }) => {
    const bars = [40, 70, 45, 90, 65, 80, 50, 85, 60, 95, 55, 75];
    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>100% <span className="text-emerald-500">Hydro-Powered</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Sustainable cloud computing powered by the Himalayas</SlideSubtitle>
                <div className="grid grid-cols-2 gap-8">
                    <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
                        className={`p-8 rounded-2xl border-2 ${isDarkMode ? 'border-emerald-500/30 bg-emerald-950/20' : 'border-emerald-200 bg-emerald-50'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <Droplets className="text-blue-500 w-7 h-7" />
                            <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-900'}`}>Renewable Energy</h3>
                        </div>
                        <p className={`mb-6 ${isDarkMode ? 'text-emerald-300/70' : 'text-emerald-700/80'}`}>
                            All three availability zones — Kathmandu, Lalitpur, and Butwal — run entirely on Nepal's abundant hydroelectric power.
                        </p>
                        <div className="flex items-end gap-1.5 h-28">
                            {bars.map((h, i) => (
                                <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ duration: 0.8, delay: 0.6 + i * 0.05 }}
                                    className={`flex-1 rounded-t ${isDarkMode ? 'bg-emerald-500/60' : 'bg-emerald-400'}`} />
                            ))}
                        </div>
                        <p className={`text-xs mt-3 ${isDarkMode ? 'text-emerald-400/50' : 'text-emerald-600/50'}`}>Hydroelectric generation capacity (MW)</p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }} className="space-y-5">
                        {[{ value: '0', unit: ' kg CO2', label: 'Carbon Emissions', desc: 'Zero fossil fuel dependency for data center operations' },
                          { value: '100', unit: '%', label: 'Renewable Energy', desc: 'Pure hydroelectric power from Himalayan rivers' },
                          { value: '40', unit: '%', label: 'Lower Energy Costs', desc: 'Himalayan climate reduces cooling requirements' }
                        ].map((stat, i) => (
                            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + i * 0.15 }}
                                className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} border`}>
                                <div className="flex items-baseline gap-2 mb-1">
                                    <span className="text-3xl font-black text-emerald-500">{stat.value}{stat.unit}</span>
                                    <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{stat.label}</span>
                                </div>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{stat.desc}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 18: MIGRATION PATH ====================
const MigrationSlide = ({ isDarkMode }) => {
    const steps = [
        { num: '01', title: 'Assess & Plan', desc: 'Infrastructure audit, dependency mapping, and migration strategy design.', icon: Target, duration: '1-2 weeks' },
        { num: '02', title: 'Pilot & Validate', desc: 'Non-production workloads migrated first. Performance benchmarking against current setup.', icon: Gauge, duration: '2-3 weeks' },
        { num: '03', title: 'Migrate & Optimize', desc: 'Production cutover with zero-downtime approach. Network, storage, and compute optimization.', icon: GitBranch, duration: '2-4 weeks' },
        { num: '04', title: 'Operate & Scale', desc: 'Handover to managed operations. 24/7 NOC/SOC monitoring activated. Continuous optimization.', icon: TrendingUp, duration: 'Ongoing' },
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-16">
            <div className="max-w-6xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Migration <span className="text-amber-500">Path</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>A structured, low-risk approach to cloud adoption</SlideSubtitle>
                <div className="relative">
                    {/* Timeline Line */}
                    <div className={`absolute left-[28px] top-8 bottom-8 w-[2px] ${isDarkMode ? 'bg-white/10' : 'bg-slate-200'}`} />
                    <motion.div initial={{ height: 0 }} animate={{ height: '100%' }} transition={{ duration: 1.5, delay: 0.5 }}
                        className="absolute left-[28px] top-8 w-[2px] bg-amber-500" style={{ maxHeight: 'calc(100% - 64px)' }} />
                    
                    <div className="space-y-6">
                        {steps.map((step, i) => (
                            <motion.div key={i} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.2 }}
                                className="flex items-start gap-6 relative">
                                <div className="w-14 h-14 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0 z-10 relative">
                                    <step.icon className="w-6 h-6 text-amber-500" />
                                </div>
                                <div className={`flex-1 p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} border`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <span className="text-amber-500/40 text-2xl font-black">{step.num}</span>
                                            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{step.title}</h3>
                                        </div>
                                        <span className={`text-xs font-mono px-3 py-1 rounded-full ${isDarkMode ? 'bg-white/5 text-slate-400' : 'bg-slate-200 text-slate-500'}`}>{step.duration}</span>
                                    </div>
                                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{step.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// ==================== SLIDE 19: PLANNER ====================
const PlannerSlide = ({ isDarkMode }) => (
    <section className="h-full w-full flex items-center justify-center px-16">
        <div className="max-w-5xl w-full text-center">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className={`inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-amber-500/20 to-blue-500/20 border ${isDarkMode ? 'border-white/10' : 'border-slate-200'} mb-8`}>
                <Cpu className="w-10 h-10 text-amber-500" />
            </motion.div>
            <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                className={`text-5xl md:text-6xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-6`}>
                EverestCloud <span className="text-amber-500">Planner</span>
            </motion.h2>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                className={`text-2xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mb-12 max-w-3xl mx-auto`}>
                Self-service cloud architecture design and sizing tool.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="grid grid-cols-3 gap-6 mb-10">
                {[{ num: "01", title: "Define Requirements", desc: "Compute, storage, network needs" },
                  { num: "02", title: "Design Architecture", desc: "Multi-AZ, DR, security layers" },
                  { num: "03", title: "Get Recommendations", desc: "Optimized configurations & pricing" }
                ].map((step, i) => (
                    <div key={i} className={`p-6 rounded-xl ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} border`}>
                        <span className="text-4xl font-black text-amber-500/30">{step.num}</span>
                        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mt-2 mb-1`}>{step.title}</h3>
                        <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>{step.desc}</p>
                    </div>
                ))}
            </motion.div>
            <motion.a 
                href="/planner" 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.8 }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold rounded-xl transition-colors"
            >
                Try the Planner <ArrowUpRight className="w-5 h-5" />
            </motion.a>
        </div>
    </section>
);

// ==================== SLIDE 20: CLOSING ====================
// ==================== SLIDE 20: CUSTOMER PORTFOLIO - BANKING & GOVERNMENT ====================
const CustomersBankingGovSlide = ({ isDarkMode }) => {
    const bankingCustomers = [
        'Agricultural Development Bank Ltd', 'Citizens Bank', 'Everest Bank Ltd.',
        'Global IME Bank Limited', 'Jyoti Bikash Bank Ltd.', 'Kamana Sewa Bikas Bank',
        'Karnali Development Bank Ltd.', 'Kumari Bank', 'Laxmi Sunrise Bank Ltd.',
        'Machhapuchchhre Bank Ltd.', 'Muktinath Bikas Bank', 'Nabil Bank',
        'Nepal Bank Ltd.', 'Nepal Infrastructure Bank', 'Nepal Investment Mega Bank',
        'Nepal SBI Bank Ltd', 'NIC ASIA BANK', 'NMB Bank', 'Prabhu Bank Ltd.',
        'Sanima Bank', 'Siddhartha Bank'
    ];
    
    const governmentCustomers = [
        'Armed Police Force (APF)', 'Credit Information Bureau', 'Department of Immigration',
        'Department of Information Technology', 'Nepal Army', 'Nepal Electricity Authority',
        'Nepal Police Headquarters', 'Public Debt Management Office', 'Supreme Court of Nepal'
    ];

    const financialServices = [
        'Esewa Pvt. Ltd.', 'F1Soft International', 'Fonepay Payment Service Ltd.',
        'IME Group', 'IME Limited', 'Kumari Capital',
        'Laxmi Laghubitta Bittiya Sanstha', 'Machhapuchchhre Capital Limited',
        'Nepal Clearing House Ltd.', 'Nepal Life Insurance Co. Ltd.',
        'Pokhara Finance Ltd.', 'Shikhar Insurance Company Limited', 'Siddhartha Capital'
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-12 py-8">
            <div className="max-w-7xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Trusted by <span className="text-amber-500">Nepal's Leaders</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Banking, Government & Financial Services</SlideSubtitle>
                
                <div className="grid grid-cols-3 gap-6">
                    {/* Banking Sector */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                        className={`p-5 rounded-2xl border-2 ${isDarkMode ? 'border-amber-500/30 bg-amber-500/5' : 'border-amber-500/50 bg-amber-50'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                                <Landmark className="w-5 h-5 text-amber-500" />
                            </div>
                            <div>
                                <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Banking Sector</h3>
                                <span className="text-amber-500 text-xs font-bold">{bankingCustomers.length} Institutions</span>
                            </div>
                        </div>
                        <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-2 custom-scrollbar">
                            {bankingCustomers.map((name, i) => (
                                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.02 }}
                                    className={`text-xs py-1.5 px-3 rounded-lg ${isDarkMode ? 'bg-white/5 text-slate-300' : 'bg-white text-slate-700'} flex items-center gap-2`}>
                                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0" />
                                    {name}
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Government Sector */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                        className={`p-5 rounded-2xl border-2 ${isDarkMode ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-emerald-500/50 bg-emerald-50'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div>
                                <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Government</h3>
                                <span className="text-emerald-500 text-xs font-bold">{governmentCustomers.length} Agencies</span>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            {governmentCustomers.map((name, i) => (
                                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.03 }}
                                    className={`text-xs py-1.5 px-3 rounded-lg ${isDarkMode ? 'bg-white/5 text-slate-300' : 'bg-white text-slate-700'} flex items-center gap-2`}>
                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0" />
                                    {name}
                                </motion.div>
                            ))}
                        </div>
                        
                    </motion.div>

                    {/* Financial Services */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                        className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-blue-500" />
                            </div>
                            <div>
                                <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Financial Services</h3>
                                <span className="text-blue-500 text-xs font-bold">{financialServices.length} Companies</span>
                            </div>
                        </div>
                        <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-2 custom-scrollbar">
                            {financialServices.map((name, i) => (
                                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.02 }}
                                    className={`text-xs py-1.5 px-3 rounded-lg ${isDarkMode ? 'bg-white/5 text-slate-300' : 'bg-white text-slate-700'} flex items-center gap-2`}>
                                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" />
                                    {name}
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Bottom Stats */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
                    className="mt-6 flex justify-center gap-8">
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}>
                        <Landmark className="w-4 h-4 text-amber-500" />
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>21 Banks Trust Us</span>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isDarkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
                        <Activity className="w-4 h-4 text-blue-500" />
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Mission Critical Projects</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

// ==================== SLIDE 21: CUSTOMER PORTFOLIO - ENTERPRISE & TECH ====================
const CustomersEnterpriseTechSlide = ({ isDarkMode }) => {
    const telecomMedia = [
        { name: 'Nepal Telecom', highlight: true },
        { name: 'Ncell Axiata Limited' },
        { name: 'Dish Media Networks' },
        { name: 'Vianet Communication Ltd.' }
    ];
    
    const itTech = [
        'Apex Solution Pvt. Ltd.', 'BIC Technology', 'Biz Serve IT Pvt. Ltd.',
        'CryptoGen Nepal', 'Infinite Software Services (Cotiviti Nepal)',
        'Neosys Technology Pvt. Ltd.', 'Rara Labs Pvt Ltd.', 'Security Pal',
        'Smart Choice Technologies', 'Verisk Nepal'
    ];
    
    const enterprise = [
        { name: 'Bottlers Nepal Limited', sector: 'FMCG' },
        { name: 'Chaudhary Group', sector: 'Conglomerate' },
        { name: 'Surya Nepal', sector: 'Conglomerate' },
        { name: 'Butwal Power Company Ltd.', sector: 'Energy' },
        { name: 'Norvic Hospital', sector: 'Healthcare' },
        { name: 'Empire Developers', sector: 'Real Estate' },
        { name: 'Salt Route Group', sector: 'Business' },
        { name: 'R2M Business Group', sector: 'Business' },
        { name: 'RSA Management Group', sector: 'Business' },
        { name: 'Tricot Industries Pvt. Ltd', sector: 'Manufacturing' },
        { name: 'Mahavir Shree International', sector: 'Business' }
    ];

    return (
        <section className="h-full w-full flex items-center justify-center px-12 py-8">
            <div className="max-w-7xl w-full">
                <SlideTitle isDarkMode={isDarkMode}>Enterprise <span className="text-amber-500">Customers</span></SlideTitle>
                <SlideSubtitle isDarkMode={isDarkMode}>Telecom, Technology & Industry Leaders</SlideSubtitle>
                
                <div className="grid grid-cols-3 gap-6">
                    {/* Telecom & Media */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                        className={`p-5 rounded-2xl border-2 ${isDarkMode ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-cyan-500/50 bg-cyan-50'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                                <Radio className="w-5 h-5 text-cyan-500" />
                            </div>
                            <div>
                                <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Telecom & Media</h3>
                                <span className="text-cyan-500 text-xs font-bold">{telecomMedia.length} Operators</span>
                            </div>
                        </div>
                        <div className="space-y-2">
                            {telecomMedia.map((item, i) => (
                                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.05 }}
                                    className={`py-2.5 px-4 rounded-xl ${item.highlight 
                                        ? (isDarkMode ? 'bg-cyan-500/20 border border-cyan-500/30' : 'bg-cyan-100 border border-cyan-300') 
                                        : (isDarkMode ? 'bg-white/5' : 'bg-white')} flex items-center gap-3`}>
                                    <span className={`w-2 h-2 rounded-full ${item.highlight ? 'bg-cyan-400 animate-pulse' : 'bg-cyan-500'}`} />
                                    <span className={`text-sm font-medium ${item.highlight ? 'text-cyan-500' : (isDarkMode ? 'text-slate-300' : 'text-slate-700')}`}>
                                        {item.name}
                                    </span>
                                    {item.highlight && <span className="text-[10px] px-2 py-0.5 bg-cyan-500 text-white rounded-full ml-auto">National Carrier</span>}
                                </motion.div>
                            ))}
                        </div>
                        
                    </motion.div>

                    {/* IT & Technology */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                        className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                                <Cpu className="w-5 h-5 text-purple-500" />
                            </div>
                            <div>
                                <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>IT & Technology</h3>
                                <span className="text-purple-500 text-xs font-bold">{itTech.length} Companies</span>
                            </div>
                        </div>
                        <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-2 custom-scrollbar">
                            {itTech.map((name, i) => (
                                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.02 }}
                                    className={`text-xs py-1.5 px-3 rounded-lg ${isDarkMode ? 'bg-white/5 text-slate-300' : 'bg-white text-slate-700'} flex items-center gap-2`}>
                                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" />
                                    {name}
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Enterprise & Industry */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                        className={`p-5 rounded-2xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                                <Briefcase className="w-5 h-5 text-rose-500" />
                            </div>
                            <div>
                                <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Enterprise</h3>
                                <span className="text-rose-500 text-xs font-bold">{enterprise.length} Organizations</span>
                            </div>
                        </div>
                        <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-2 custom-scrollbar">
                            {enterprise.map((item, i) => (
                                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.02 }}
                                    className={`text-xs py-1.5 px-3 rounded-lg ${isDarkMode ? 'bg-white/5 text-slate-300' : 'bg-white text-slate-700'} flex items-center justify-between`}>
                                    <div className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-rose-500 rounded-full flex-shrink-0" />
                                        {item.name}
                                    </div>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-white/10 text-slate-400' : 'bg-slate-200 text-slate-500'}`}>
                                        {item.sector}
                                    </span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Bottom Summary */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
                    className="mt-6 flex justify-center gap-6">
                    <div className={`text-center px-6 py-3 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
                        <div className={`text-3xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>100+</div>
                        <div className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Total Customers</div>
                    </div>
                    <div className={`text-center px-6 py-3 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
                        <div className={`text-3xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>200+</div>
                        <div className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Projects</div>
                    </div>
                    <div className={`text-center px-6 py-3 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
                        <div className={`text-3xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>10+</div>
                        <div className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Industry Sectors</div>
                    </div>
                    <div className={`text-center px-6 py-3 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
                        <div className={`text-3xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>18+</div>
                        <div className={`text-xs uppercase tracking-wider ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Years of Trust</div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

const ClosingSlide = ({ isDarkMode }) => (
    <section className="h-full w-full flex items-center justify-center px-16 relative">
        <div className="absolute inset-0 bg-cover bg-no-repeat opacity-15" style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_everestcloud/artifacts/spue5mbw_backgroundjpeg.jpg')", backgroundPosition: "center 30%" }} />
        <div className="max-w-4xl w-full text-center relative z-10">
            <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className={`text-5xl md:text-7xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-8`}>
                Ready to Build on<br /><span className="text-amber-500">Nepal's Cloud?</span>
            </motion.h2>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                className={`text-2xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'} mb-12`}>
                Let's discuss how EverestCloud can power your enterprise.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="flex items-center justify-center gap-6">
                <a href="/contact" className="px-8 py-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-lg transition-all flex items-center gap-2">
                    Request Demo <ArrowRight className="w-5 h-5" />
                </a>
                <a href="/platform" className={`px-8 py-4 rounded-xl ${isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white border-white/20' : 'bg-slate-100 hover:bg-slate-200 text-slate-900 border-slate-300'} font-bold text-lg transition-all border flex items-center gap-2`}>
                    View Platform <Play className="w-5 h-5" />
                </a>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                className={`mt-16 pt-8 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-600'}`}>EverestCloud | Nepal's Sovereign Enterprise Cloud</p>
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`}>www.everestcloud.com.np</p>
            </motion.div>
        </div>
    </section>
);

export default PresentationMode;
